
/**
 * SectionScroll for lbbw-am.de
 *
 * Based on jQuery Section Scroll v2
 * Original: https://github.com/sylvainbaronnet
 * Converted to Plain JS for Rollup
 */
export default class SectionScroll {
  /**
   * SectionScroll constructor
   *
   * @param {HTMLElement} element - The container element
   * @param {Object} options - Configuration options
   */
  constructor(element, options = {}) {
    this.element = element;
    this.window = window;
    this.lastId = null;
    this.activeSection = null;

    // Default settings
    this.settings = Object.assign({
      bulletsClass: 'section-bullets',
      sectionsClass: 'scrollable-section',
      scrollDuration: 1000,
      titles: true,
      topOffset: 0,
      easing: 'ease-in-out'
    }, options);

    this.sections = Array.from(document.querySelectorAll('.' + this.settings.sectionsClass));

    if (this.sections.length === 0) {
      console.warn('SectionScroll: No sections found with class', this.settings.sectionsClass);
      return;
    }

    this.init();
  }

  /**
   * Initialize the section scroll
   */
  init() {
    this.buildNavigation();
    this.attachScrollHandler();
    this.attachClickHandlers();

    // Trigger initial scroll check
    setTimeout(() => this.handleScroll(), 100);
  }

  /**
   * Build the navigation bullets
   */
  buildNavigation() {
    // Create bullets container
    const bulletsContainer = document.createElement('div');
    bulletsContainer.className = 'bullets-container';

    const bulletsList = document.createElement('ul');
    bulletsList.className = this.settings.bulletsClass;

    bulletsContainer.appendChild(bulletsList);
    this.element.insertBefore(bulletsContainer, this.element.firstChild);

    // Build navigation items
    this.sections.forEach((section, index) => {
      const sectionNumber = index + 1;
      const title = section.dataset.sectionTitle || '';
      const sectionId = `scrollto-section-${sectionNumber}`;

      // Set section ID
      section.id = sectionId;

      // Create bullet item
      const bulletItem = document.createElement('li');
      const bulletLink = document.createElement('a');

      bulletLink.title = title;
      bulletLink.href = `#${sectionId}`;

      if (this.settings.titles) {
        const titleSpan = document.createElement('span');
        titleSpan.textContent = title;
        bulletLink.appendChild(titleSpan);
      }

      bulletItem.appendChild(bulletLink);
      bulletsList.appendChild(bulletItem);
    });

    this.bulletItems = Array.from(bulletsList.querySelectorAll('li'));
    this.bulletLinks = Array.from(bulletsList.querySelectorAll('a'));
  }

  /**
   * Attach click handlers to bullets
   */
  attachClickHandlers() {
    this.bulletItems.forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();

        const link = item.querySelector('a');
        const href = link.getAttribute('href');
        const targetSection = document.querySelector(href);

        if (targetSection) {
          const offsetTop = targetSection.offsetTop;
          this.smoothScrollTo(offsetTop - this.settings.topOffset);
        }
      });
    });
  }

  /**
   * Smooth scroll to position
   * @param {number} targetPosition
   */
  smoothScrollTo(targetPosition) {
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    const duration = this.settings.scrollDuration;
    let startTime = null;

    const animation = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);

      // Easing function (ease-in-out)
      const ease = progress < 0.5
        ? 2 * progress * progress
        : -1 + (4 - 2 * progress) * progress;

      window.scrollTo(0, startPosition + distance * ease);

      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      } else {
        // Trigger custom event
        this.element.dispatchEvent(new CustomEvent('scrolled-to-section', {
          bubbles: true
        }));
      }
    };

    requestAnimationFrame(animation);
  }

  /**
   * Attach scroll handler
   */
  attachScrollHandler() {
    let scrollTimeout;

    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => this.handleScroll(), 10);
    });
  }

  /**
   * Handle scroll event
   */
  handleScroll() {
    const fromTop = window.pageYOffset + (window.innerHeight / 2.5);

    // Find current section
    let currentSection = null;

    for (let i = this.sections.length - 1; i >= 0; i--) {
      const section = this.sections[i];
      if (section.offsetTop < fromTop) {
        currentSection = section;
        break;
      }
    }

    if (!currentSection) {
      currentSection = this.sections[0];
    }

    const currentId = currentSection ? currentSection.id : '';

    // Update active states if section changed
    if (this.lastId !== currentId) {
      // Remove active class from all sections
      this.sections.forEach(section => {
        section.classList.remove('active-section');
      });

      // Add active class to current section
      if (currentSection) {
        currentSection.classList.add('active-section');
      }

      // Update bullet navigation
      this.bulletItems.forEach(item => {
        item.classList.remove('active');
        const link = item.querySelector('a');
        if (link.getAttribute('href') === `#${currentId}`) {
          item.classList.add('active');
        }
      });

      this.lastId = currentId;
      this.activeSection = currentSection;

      // Trigger custom event
      this.element.dispatchEvent(new CustomEvent('section-reached', {
        bubbles: true,
        detail: { section: currentSection }
      }));
    }
  }

  /**
   * Get the currently active section
   * @returns {HTMLElement|null}
   */
  getActiveSection() {
    return this.activeSection;
  }

  /**
   * Scroll to a specific section by index
   * @param {number} index
   */
  scrollToSection(index) {
    if (index >= 0 && index < this.sections.length) {
      const section = this.sections[index];
      const offsetTop = section.offsetTop;
      this.smoothScrollTo(offsetTop - this.settings.topOffset);
    }
  }

  /**
   * Destroy the section scroll
   */
  destroy() {
    // Remove bullets container
    const bulletsContainer = this.element.querySelector('.bullets-container');
    if (bulletsContainer) {
      bulletsContainer.remove();
    }

    // Remove active classes
    this.sections.forEach(section => {
      section.classList.remove('active-section');
    });

    // Note: We don't remove scroll listeners as they're on window
    // In a production app, you'd want to store references and remove them
  }
}

// Auto-init for elements with data attribute
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('[data-section-scroll]');
  containers.forEach(container => {
    const options = container.dataset.sectionScrollOptions
      ? JSON.parse(container.dataset.sectionScrollOptions)
      : {};
    new SectionScroll(container, options);
  });
});